/** The hive's sounds (04 §5): hits, kills, the dive whistle, bombs, morphs, bonuses. Pure. */

import { midiToHz } from '../notes';
import { intBelow, vary } from '../rand';
import { DIVING } from '../scan';
import { adsr, type VoiceSpec } from '../voice';
import { hit, type Patch, type Sfx, tone, tune } from './kit';

/** A Monarch's first hit: a metallic clank - three partials no harmonic series owns, the top dying first. */
export const enemyHit: Sfx = (r) => {
  const hz = vary(r, 587, 0.03);
  return [
    { ...hit('pulse', hz, 0.13, 0.2), width: 0.25 },
    { ...hit('pulse', hz * 1.483, 0.1, 0.14), width: 0.5 },
    hit('triangle', hz * 2.76, 0.06, 0.12),
  ];
};

/** Where each kind's falling tone starts, Hz, by `ENEMY_RANK`: Stinger, Moth, morph, Monarch. */
export const KILL_HZ = [1480, 1175, 1320, 784] as const;

/**
 * An enemy destroyed: a pop of noise snapping shut over a pulse falling two octaves - punchy,
 * all attack. `n` is the kind's rank, plus `DIVING` for one shot out of a dive, which is louder
 * and longer with a low thump under it; a Monarch's is half as long again. Three voices at most:
 * kills come thick and fast, and each one's tail is the next one's room.
 */
export const kill: Sfx = (r, n) => {
  const diving = n >= DIVING;
  const rank = n % DIVING;
  const hz = vary(r, KILL_HZ[rank] ?? KILL_HZ[0], 0.05);
  const long = (diving ? 0.19 : 0.13) * (rank === 3 ? 1.5 : 1);
  const voices: VoiceSpec[] = [
    {
      ...hit('noise', vary(r, 6000, 0.1), long, diving ? 0.55 : 0.45),
      to: 700,
      glide: long,
      filter: { type: 'lowpass', freq: 8000, to: 700, sweep: long, q: 2 },
    },
    { ...hit('pulse', hz, long + 0.03, diving ? 0.24 : 0.2), width: 0.25, to: hz / 4, glide: long },
  ];
  if (diving) voices.push({ ...hit('triangle', vary(r, 190, 0.08), 0.18, 0.42), to: 48, glide: 0.16 });
  return voices;
};

const SAD: Patch = { wave: 'triangle', env: adsr(0.004, 0.1, 0.7, 0.03), level: 0.24 };

/** The captured fighter shot by mistake: a small pop, then a sad little fall down A minor that sags away. */
export const captiveKilled: Sfx = (r) => [
  {
    ...hit('noise', vary(r, 5000, 0.1), 0.09, 0.3),
    filter: { type: 'lowpass', freq: 5000, to: 900, sweep: 0.09, q: 1 },
  },
  ...tune('E5 . C5 . A4 .', 0.065, SAD),
  {
    ...tone('triangle', midiToHz(64), 0.32, adsr(0.004, 0.2, 0.5, 0.1), 0.24),
    at: 0.39,
    to: midiToHz(57),
    glide: 0.32,
    vibrato: { rate: 6, depth: 30, delay: 0.05 },
  },
];

/** Where each kind's whistle starts, Hz, by `ENEMY_RANK`: B6, G#6, A6, E6. */
export const DIVE_HZ = [1976, 1661, 1760, 1319] as const;

/**
 * The falling whistle of a dive, the game's signature: a triangle gliding down an octave over
 * half a second, with a quick shiver in it. `n` is the kind's rank: a Monarch's starts lowest.
 */
export const dive: Sfx = (r, n) => {
  const hz = vary(r, DIVE_HZ[n] ?? DIVE_HZ[0], 0.03);
  return [{
    ...tone('triangle', hz, 0.5, adsr(0.012, 0.15, 0.75, 0.07), 0.2),
    to: hz / 2,
    glide: 0.5,
    vibrato: { rate: 15, depth: 30, delay: 0 },
  }];
};

/** A bomb let go: a quiet tick, a thin pulse dropping an octave in a twentieth of a second. */
export const bomb: Sfx = (r) => [
  { ...hit('pulse', vary(r, 560, 0.1), 0.045, 0.15), width: 0.2, to: 250, glide: 0.045 },
];

/** A Stinger begins to change: a thin pulse bubbling up and down a diminished chord. */
export const morph: Sfx = (r) => {
  const base = 76 + intBelow(r, 3);
  const steps = [0, 6, 3, 9, 6, 12].map((s) => midiToHz(base + s));
  return [{
    ...tone('pulse', steps[0] ?? 660, 0.36, adsr(0.004, 0.1, 0.7, 0.08), 0.2),
    width: 0.125,
    steps,
    stepTime: 0.03,
  }];
};

/** A chirp: a pulse sweeping up two octaves in a seventh of a second. */
function chirp(hz: number, at: number): VoiceSpec {
  return {
    ...tone('pulse', hz, 0.14, adsr(0.002, 0.08, 0.6, 0.06), 0.18),
    at,
    width: 0.25,
    to: hz * 4,
    glide: 0.14,
  };
}

/** A whole wave shot down: one rising chirp. */
export const waveBonus: Sfx = (r) => [chirp(vary(r, 660, 0.02), 0)];

/** A whole morph trio shot down: the chirp twice, the second a fourth higher. */
export const morphBonus: Sfx = (r) => {
  const hz = vary(r, 660, 0.02);
  return [chirp(hz, 0), chirp(hz * 4 / 3, 0.11)];
};
